import React, { useEffect, useState } from "react";
import {
  Card, Form, Input, Button, Row, Col, Tag, Space, Typography, Progress,
  message, Spin, Divider,
} from "antd";
import {
  UserOutlined, MailOutlined, PhoneOutlined, IdcardOutlined,
  SafetyOutlined, BankOutlined,
} from "@ant-design/icons";
import { useAuth } from "../../context/AuthContext";
import { getMyProfile, updateMyProfile, cambiarPassword } from "../../api/Auth";

const { Title, Text } = Typography;

const calcularFuerza = (password) => {
  let fuerza = 0;
  if (!password) return 0;
  if (password.length >= 8) fuerza += 1;
  if (/[A-Z]/.test(password)) fuerza += 1;
  if (/[a-z]/.test(password)) fuerza += 1;
  if (/\d/.test(password)) fuerza += 1;
  if (/[\W_]/.test(password)) fuerza += 1;
  return fuerza;
};
const fuerzaToStatus = (f) => (f <= 1 ? "exception" : f <= 3 ? "normal" : "success");

const ProfilePage = () => {
  const { updateUser } = useAuth();
  const [perfilForm] = Form.useForm();
  const [pwdForm] = Form.useForm();

  const [loading, setLoading] = useState(true);
  const [savingPerfil, setSavingPerfil] = useState(false);
  const [savingPwd, setSavingPwd] = useState(false);
  const [info, setInfo] = useState({ roles: [], empresa: null, username: "" });
  const [newPassword, setNewPassword] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const data = await getMyProfile();
        perfilForm.setFieldsValue({
          username: data.username,
          first_name: data.first_name,
          last_name: data.last_name,
          email: data.email,
          telefono: data.telefono,
        });
        setInfo({ roles: data.roles || [], empresa: data.empresa, username: data.username });
      } catch (e) {
        message.error("No se pudo cargar tu perfil.");
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleGuardarPerfil = async (values) => {
    setSavingPerfil(true);
    const res = await updateMyProfile({
      first_name: values.first_name,
      last_name: values.last_name,
      email: values.email,
      telefono: values.telefono || "",
    });
    setSavingPerfil(false);
    if (res.success) {
      message.success("Perfil actualizado con éxito.");
      // Reflejar nombre/email en el resto de la app
      updateUser({
        email: res.data.email,
        nombre: res.data.first_name,
        apellido: res.data.last_name,
        telefono: res.data.telefono,
      });
    } else {
      message.error(res.message);
    }
  };

  const handleCambiarPwd = async (values) => {
    setSavingPwd(true);
    const res = await cambiarPassword(values.current_password, values.new_password);
    setSavingPwd(false);
    if (res.success) {
      message.success(res.message);
      pwdForm.resetFields();
      setNewPassword("");
    } else {
      message.error(res.message);
    }
  };

  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", padding: 80 }}>
        <Spin size="large" />
      </div>
    );
  }

  const fuerza = calcularFuerza(newPassword);

  return (
    <div style={{ maxWidth: 820, margin: "0 auto", padding: 24 }}>
      <Title level={3} style={{ marginBottom: 4 }}>Mi perfil</Title>
      <Text type="secondary">Administra tu información personal y tu contraseña.</Text>

      <Row gutter={[16, 16]} style={{ marginTop: 20 }}>
        {/* DATOS PERSONALES */}
        <Col xs={24} md={14}>
          <Card title={<span><IdcardOutlined /> Datos personales</span>}>
            <Form form={perfilForm} layout="vertical" onFinish={handleGuardarPerfil}>
              <Form.Item name="username" label="Usuario"
                tooltip="El nombre de usuario no se puede cambiar.">
                <Input prefix={<UserOutlined />} disabled />
              </Form.Item>

              <Row gutter={12}>
                <Col xs={24} sm={12}>
                  <Form.Item name="first_name" label="Nombre"
                    rules={[{ required: true, message: "Ingresa tu nombre" }]}>
                    <Input />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item name="last_name" label="Apellido"
                    rules={[{ required: true, message: "Ingresa tu apellido" }]}>
                    <Input />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item name="email" label="Email"
                rules={[
                  { required: true, message: "Ingresa tu email" },
                  { type: "email", message: "Ingresa un correo válido" },
                ]}>
                <Input prefix={<MailOutlined />} />
              </Form.Item>

              <Form.Item name="telefono" label="Teléfono">
                <Input prefix={<PhoneOutlined />} placeholder="Opcional" maxLength={20} />
              </Form.Item>

              <Button type="primary" htmlType="submit" loading={savingPerfil}>
                Guardar cambios
              </Button>
            </Form>
          </Card>
        </Col>

        {/* INFO SOLO LECTURA + SEGURIDAD */}
        <Col xs={24} md={10}>
          <Card title={<span><BankOutlined /> Cuenta</span>} style={{ marginBottom: 16 }}>
            <Space direction="vertical" size="small" style={{ width: "100%" }}>
              <div>
                <Text type="secondary">Roles</Text>
                <div>
                  {info.roles.length
                    ? info.roles.map((r) => <Tag color="blue" key={r}>{r}</Tag>)
                    : <Text type="secondary">Sin roles</Text>}
                </div>
              </div>
              <div>
                <Text type="secondary">Empresa</Text>
                <div>{info.empresa ? <Tag icon={<BankOutlined />}>{info.empresa}</Tag> : <Text type="secondary">—</Text>}</div>
              </div>
              <Text type="secondary" style={{ fontSize: 12 }}>
                Roles y empresa los gestiona un administrador.
              </Text>
            </Space>
          </Card>

          <Card title={<span><SafetyOutlined /> Cambiar contraseña</span>}>
            <Form form={pwdForm} layout="vertical" onFinish={handleCambiarPwd}>
              <Form.Item name="current_password" label="Contraseña actual"
                rules={[{ required: true, message: "Ingresa tu contraseña actual" }]}>
                <Input.Password autoComplete="current-password" />
              </Form.Item>

              <Form.Item name="new_password" label="Nueva contraseña"
                rules={[
                  { required: true, message: "Ingresa la nueva contraseña" },
                  {
                    pattern: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\W_]).{8,}$/,
                    message: "8+ caracteres, mayúscula, minúscula, número y símbolo",
                  },
                ]}>
                <Input.Password autoComplete="new-password"
                  onChange={(e) => setNewPassword(e.target.value)} />
              </Form.Item>

              {newPassword && (
                <div style={{ marginTop: -12, marginBottom: 12 }}>
                  <Progress percent={(fuerza / 5) * 100} status={fuerzaToStatus(fuerza)} showInfo={false} />
                  <div style={{ textAlign: "right", fontSize: 12 }}>
                    {["Muy débil", "Débil", "Aceptable", "Buena", "Fuerte"][fuerza - 1] || "Muy débil"}
                  </div>
                </div>
              )}

              <Form.Item name="confirm_password" label="Confirmar nueva contraseña"
                dependencies={["new_password"]}
                rules={[
                  { required: true, message: "Confirma la nueva contraseña" },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value || getFieldValue("new_password") === value) return Promise.resolve();
                      return Promise.reject(new Error("Las contraseñas no coinciden"));
                    },
                  }),
                ]}>
                <Input.Password autoComplete="new-password" />
              </Form.Item>

              <Button type="primary" htmlType="submit" loading={savingPwd}>
                Cambiar contraseña
              </Button>
            </Form>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default ProfilePage;
